import {
  API_ORIGIN,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CopyPublicLinkButton,
  EntityDetailSkeleton,
  PersonProfileStandingView,
  PublicPageHead,
  PublicShareLayout,
  authorizedFetch,
  parseApiErrorMessage,
  useAuth,
  usePublicShareRouteParam,
} from '@yeshuman/sdk'
import type { PublicProfile } from '@yeshuman/sdk'
import { Lock } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { useFillViewportHeight } from '../../hooks/use-fill-viewport-height'
import { publicProfileToStandingData } from '../../lib/public-profile-standing'

/**
 * `/p/:slug` for TalentCo. Unlike the platform page it sends the viewer's token,
 * so the API visibility gate (`candidates.privacy`) can show restricted profiles
 * to signed-in employers while guests get a private-profile message.
 */
export function GatedPublicProfilePage() {
  const slug = usePublicShareRouteParam('/p/:slug', 'slug')
  const { token } = useAuth()
  const signedIn = Boolean(token)
  const [profile, setProfile] = useState<PublicProfile | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError(null)
      setProfile(null)
      if (!slug) {
        setError('Profile not found')
        setLoading(false)
        return
      }
      try {
        const response = await authorizedFetch(
          `${API_ORIGIN}/api/public/profiles/${encodeURIComponent(slug)}`,
        )
        if (!response.ok) {
          throw new Error(await parseApiErrorMessage(response, 'Profile not found'))
        }
        const data = (await response.json()) as PublicProfile
        if (!cancelled) setProfile(data)
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Profile not found')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [slug, token])

  const standing = useMemo(() => (profile ? publicProfileToStandingData(profile) : null), [profile])

  if (loading) {
    return (
      <PublicShareLayout>
        <EntityDetailSkeleton variant="profile" withPageContainer={false} ariaLabel="Loading profile" />
      </PublicShareLayout>
    )
  }

  if (error || !profile || !standing) {
    return (
      <PublicShareLayout>
        <PrivateProfileNotice signedIn={signedIn} />
      </PublicShareLayout>
    )
  }

  const displayName = profile.full_name || 'Profile'
  const publicUrl = profile.public_url || `${window.location.origin}/p/${profile.slug}`

  return (
    <PublicShareLayout>
      <PublicPageHead
        title={displayName}
        description={profile.headline || profile.bio?.slice(0, 200) || undefined}
        imageUrl={profile.avatar_url}
        canonicalPath={`/p/${profile.slug}`}
      />
      <div className="space-y-4">
        <div className="flex justify-end">
          <CopyPublicLinkButton url={publicUrl} />
        </div>
        <PersonProfileStandingView profile={standing} skillsVariant="public" />
      </div>
    </PublicShareLayout>
  )
}

function PrivateProfileNotice({ signedIn }: { signedIn: boolean }) {
  const { ref, minHeight } = useFillViewportHeight<HTMLDivElement>()
  const returnTo = `${window.location.pathname}${window.location.search}`
  return (
    <div
      ref={ref}
      style={{ minHeight }}
      className="flex items-center justify-center py-8"
      data-testid="private-profile-wrapper"
    >
      <PublicPageHead title="Private profile" description="This TalentCo profile is private." />
      <Card className="w-full max-w-lg text-center" data-testid="private-profile-card">
        <CardHeader className="items-center">
          <span className="mx-auto mb-2 flex size-12 items-center justify-center rounded-full bg-muted">
            <Lock className="size-5" aria-hidden />
          </span>
          <CardTitle>This profile is private</CardTitle>
          <CardDescription alwaysShow>
            {signedIn
              ? 'This candidate has not shared a public link, and their profile is only visible to employers on TalentCo. It may also have been removed.'
              : 'TalentCo profiles are only visible to signed-in employers unless the candidate shares a public link.'}
          </CardDescription>
        </CardHeader>
        {!signedIn ? (
          <CardContent className="flex justify-center">
            <Button asChild>
              <a href={`/login?returnTo=${encodeURIComponent(returnTo)}`}>Sign in as an employer</a>
            </Button>
          </CardContent>
        ) : null}
      </Card>
    </div>
  )
}
